import React, { useCallback, useState } from 'react';
import {
  useAppMessage,
  useAudioTrack,
  useDaily,
  useLocalSessionId,
  useScreenShare,
  useVideoTrack,
} from '@daily-co/daily-react';
import { useRecoilState } from 'recoil';
import { Tooltip } from 'react-tooltip';

import MeetingInformation from '../MeetingInformation/MeetingInformation';

import './Tray.css';
import {
  CameraOn,
  Leave,
  CameraOff,
  MicrophoneOff,
  MicrophoneOn,
  Screenshare,
  // Info,
  ChatIcon,
  ChatHighlighted,
  TeacherViewScreens,
  ParticipantsIcon,
  ParticipantCameras,
  ScreenshareOff,
} from './Icons';
import { showParticipantsWindowState, teacherIdState } from '../../state/participantAtom';
import { teacherScreenViewState } from '../../state/viewModeAtom';
import { showChatWindowState, newChatMessageState } from '../../state/chatAtom';
import ViewMode from '../../Constants/viewModes';
import useIsMobile from '../hooks/useIsMobile';

export default function Tray({ leaveCall }) {
  const callObject = useDaily();
  const { isSharingScreen, startScreenShare, stopScreenShare } = useScreenShare();

  const isMobile = useIsMobile(768);

  const [showMeetingInformation] = useState(false);
  const [showChat, setShowChat] = useRecoilState(showChatWindowState);
  const [showParticipants, setShowParticipants] = useRecoilState(showParticipantsWindowState);
  const [teacherId] = useRecoilState(teacherIdState);
  const [newChatMessage, setNewChatMessage] = useRecoilState(newChatMessageState);

  const localSessionId = useLocalSessionId();
  const localVideo = useVideoTrack(localSessionId);
  const localAudio = useAudioTrack(localSessionId);
  const mutedVideo = localVideo.isOff;
  const mutedAudio = localAudio.isOff;
  const isTeacher = useLocalSessionId() === teacherId;
  /* When a remote participant sends a message in the chat, we want to display a differently colored
   * chat icon in the Tray as a notification. By listening for the `"app-message"` event we'll know
   * when someone has sent a message. */
  const [viewMode, setViewMode] = useRecoilState(teacherScreenViewState);

  useAppMessage({
    onAppMessage: useCallback(() => {
      /* Only light up the chat icon if the chat isn't already open. */
      if (!showChat) {
        setNewChatMessage(true);
      }
    }, [showChat]),
  });

  const toggleVideo = useCallback(() => {
    callObject.setLocalVideo(mutedVideo);
  }, [callObject, mutedVideo]);

  const toggleAudio = useCallback(() => {
    callObject.setLocalAudio(mutedAudio);
  }, [callObject, mutedAudio]);

  const toggleScreenShare = () => (isSharingScreen ? stopScreenShare() : startScreenShare());

  const toggleTeacherViewScreens = () => {
    setViewMode((prev) => (prev === ViewMode.CALL ? ViewMode.GRID : ViewMode.CALL));
  };

  // const toggleMeetingInformation = () => {
  //   setShowMeetingInformation(!showMeetingInformation);
  // };

  const toggleChat = () => {
    if (showParticipants) {
      setShowParticipants(!showParticipants);
    }
    setShowChat(!showChat);
    if (newChatMessage) {
      setNewChatMessage(!newChatMessage);
    }
  };

  const toggleParticipants = () => {
    if (showChat) {
      setShowChat(!showChat);
    }
    setShowParticipants(!showParticipants);
  };

  if (isMobile) {
    return (
      <div className="tray">
        {showMeetingInformation && <MeetingInformation />}
        <div className="tray-mobile-buttons-container">
          <div className="tray-mobile-button">
            <button
              onClick={toggleVideo}
              type="button"
              data-tooltip-id="video-tooltip"
              data-tooltip-content="Įjungti / Išjungti kamerą">
              {mutedVideo ? <CameraOff /> : <CameraOn />}
            </button>
            <Tooltip id="video-tooltip" place="top" delayShow={200} float={false} />
          </div>

          <div className="tray-mobile-button">
            <button
              onClick={toggleAudio}
              type="button"
              data-tooltip-id="audio-tooltip"
              data-tooltip-content="Įjungti / Išjungti mikrofoną">
              {mutedAudio ? <MicrophoneOff /> : <MicrophoneOn />}
            </button>
            <Tooltip id="audio-tooltip" place="top" delayShow={200} float={false} />
          </div>

          <div className="tray-mobile-button">
            <button
              onClick={toggleChat}
              type="button"
              data-tooltip-id="chat-tooltip"
              data-tooltip-content="Atidaryti pokalbį">
              {newChatMessage ? <ChatHighlighted /> : <ChatIcon />}
            </button>
            <Tooltip id="chat-tooltip" place="bottom" delayShow={200} float={false} />
          </div>

          <div className="tray-mobile-button">
            <button
              onClick={leaveCall}
              type="button"
              data-tooltip-id="leave-tooltip"
              data-tooltip-content="Palikti skambutį">
              <Leave />
            </button>
            <Tooltip id="leave-tooltip" place="top" delayShow={200} float={false} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tray">
      {showMeetingInformation && <MeetingInformation />}
      <div className="tray-buttons-container">
        <div className="controls">
          <button
            onClick={toggleVideo}
            type="button"
            data-tooltip-id="video-tooltip"
            data-tooltip-content="Įjungti / Išjungti kamerą">
            {mutedVideo ? <CameraOff /> : <CameraOn />}
          </button>
          <Tooltip id="video-tooltip" place="top" delayShow={200} float={false} />

          <button
            onClick={toggleAudio}
            type="button"
            data-tooltip-id="audio-tooltip"
            data-tooltip-content="Įjungti / Išjungti mikrofoną">
            {mutedAudio ? <MicrophoneOff /> : <MicrophoneOn />}
          </button>
          <Tooltip id="audio-tooltip" place="top" delayShow={200} float={false} />
        </div>

        <div className="actions">
          {isTeacher && !isMobile && (
            <button
              onClick={toggleTeacherViewScreens}
              type="button"
              data-tooltip-id="teacher-view-tooltip"
              data-tooltip-content="Perjungti mokytojo rodinį">
              {viewMode === ViewMode.CALL ? (
                <TeacherViewScreens onClick={toggleTeacherViewScreens} />
              ) : (
                <ParticipantCameras />
              )}
            </button>
          )}
          <Tooltip id="teacher-view-tooltip" place="top" delayShow={200} float={false} />

          {!isMobile && (
            <button
              onClick={toggleScreenShare}
              type="button"
              data-tooltip-id="screen-share-tooltip"
              data-tooltip-content={
                isSharingScreen ? 'Nutraukti dalinimąsi ekranu' : 'Dalintis ekranu'
              }>
              {isSharingScreen ? <ScreenshareOff /> : <Screenshare />}
            </button>
          )}
          <Tooltip id="screen-share-tooltip" place="top" delayShow={200} float={false} />

          <button
            onClick={toggleChat}
            type="button"
            data-tooltip-id="chat-tooltip"
            data-tooltip-content="Atidaryti pokalbį">
            {newChatMessage ? <ChatHighlighted /> : <ChatIcon />}
          </button>
          <Tooltip id="chat-tooltip" place="bottom" delayShow={200} float={false} />

          {!isMobile && (
            <button
              onClick={toggleParticipants}
              type="button"
              data-tooltip-id="participants-tooltip"
              data-tooltip-content="Dalyviai">
              <ParticipantsIcon />
            </button>
          )}
          <Tooltip id="participants-tooltip" place="top" delayShow={200} float={false} />
        </div>

        <div className="leave">
          <button
            onClick={leaveCall}
            type="button"
            data-tooltip-id="leave-tooltip"
            data-tooltip-content="Palikti skambutį">
            <Leave />
          </button>
          <Tooltip id="leave-tooltip" place="top" delayShow={200} float={false} />
        </div>
      </div>
    </div>
  );
}
